import { GameContext } from '../../../contexts/GameContext';
import { GameStat } from '../../../gql-types';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { StopWatchContext } from '../../../contexts/StopWatchContext';
import {
    ZdarGameStepConfiguration,
    zdarEscapeGameNumberSteps,
} from '../../../configuration/games/zdar-escape-game/zdarEscapeGameContentConfiguration';
import { isEmptyObject } from '../../../util/isEmptyObject';
import { useContext, useEffect, useState } from 'react';
import { useGraphQL } from '../../../hooks/useGraphQL';
import { useTime } from '../../../hooks/useTime';
import ContinueButton from '../../../components/common/buttons/ContinueButton';
import GameAudioPlayer from '../../../components/common/GameAudioPlayer';
import GameWrapper from '../../../components/common/wrappers/GameWrapper';
import Loading from '../../../components/common/Loading';
import ZdarCipher from '../../../components/games/zdar-escape-game/ZdarCipher';
import ZdarCodeInput from '../../../components/games/zdar-escape-game/ZdarCodeInput';
import ZdarEnding from './ZdarEnding';
import ZdarHeader from '../../../components/games/zdar-escape-game/ZdarHeader';
import ZdarHints from '../../../components/games/zdar-escape-game/ZdarHints';
import ZdarTaskText from '../../../components/games/zdar-escape-game/ZdarTaskText';
import ZdarTaskTitle from '../../../components/games/zdar-escape-game/ZdarTaskTitle';

const DEFAULT_STATS: GameStat = {
    timeSpent: '0',
    hintsTaken: 0,
    mistakes: 0,
    audioPlayed: false,
    codes: [],
    points: 0,
    mapClicked: false,
    reportClicked: false,
};

function ZdarGame() {
    const { startGame, saveProgress, savePenalty, endGame } = useGraphQL();
    const [audioOpened, setAudioOpened] = useState(false);
    const [currentStepStats, setCurrentStepStats] = useState(DEFAULT_STATS);
    const [currentStepStartTimestamp, setCurrentStepStartTimestamp] = useState(0);
    const { getTimeSplit } = useTime();
    const [loading, setLoading] = useState(false);
    const [pageCatImage, setPageCatImage] = useState(1);

    const { availableGame } = useContext(ProfileContext);

    const { lastStep, configuration, currentStep, nextStepAction, stepContent, setStepContent, setCurrentStep } =
        useContext(GameContext);
    const { saveHint, wrongAnswer, saveSolutionPenalty } = useContext(StopWatchContext);

    const {
        title,
        taskText,
        taskPreviewText,
        continueButtonText,
        codeInputText,
        codeInputPassCodeRegexp,
        audio,
        snowAudio,
        catImage,
        cipher,
        snowTaskText,
    } = stepContent as ZdarGameStepConfiguration;

    useEffect(() => {
        if (catImage) {
            setPageCatImage(catImage);
        }
    }, [catImage]);

    useEffect(() => {
        if (configuration) {
            if (availableGame) {
                setCurrentStep((availableGame.lastStep ?? 0) + 1);
                if (availableGame.lastStep === currentStep) {
                    setLoading(false);
                }
                setStepContent(configuration[(availableGame.lastStep ?? 0) + 1]);
            } else {
                setStepContent(configuration[currentStep]);
            }
        }
    }, [configuration, currentStep, availableGame]);

    useEffect(() => {
        setCurrentStepStats({
            ...DEFAULT_STATS,
            codes: [],
        });
        setCurrentStepStartTimestamp(Math.floor(new Date().getTime() / 1000));
        setAudioOpened(false);
    }, [currentStep]);

    const onOpenAudio = (value: boolean) => {
        setCurrentStepStats({
            ...currentStepStats,
            audioPlayed: true,
        });
        setAudioOpened(value);
    };

    const onWrongAnswer = (code: string) => {
        const newCodes = currentStepStats.codes;
        newCodes?.push(code);
        setCurrentStepStats({
            ...currentStepStats,
            mistakes: (currentStepStats.mistakes || 0) + 1,
            codes: newCodes,
        });
        wrongAnswer(savePenalty);
    };

    const onConfirm = () => {
        // @ts-expect-error
        setStepContent({});
        setLoading(true);
        if (currentStep > 1) {
            const end = Math.floor(new Date().getTime() / 1000);
            const { getSeconds, getMinutes, getHours } = getTimeSplit(end - currentStepStartTimestamp);
            saveProgress(
                currentStep,
                {
                    ...currentStepStats,
                    timeSpent: getHours + ':' + getMinutes + ':' + getSeconds,
                },
                availableGame?.token ?? null,
            );
            nextStepAction({});
        }
        if (currentStep === 1) {
            nextStepAction({ availableGame, startGame });
        }
        if (currentStep === lastStep) {
            const end = Math.floor(new Date().getTime() / 1000);
            const { getSeconds, getMinutes, getHours } = getTimeSplit(end - currentStepStartTimestamp);
            saveProgress(
                currentStep,
                {
                    ...currentStepStats,
                    timeSpent: getHours + ':' + getMinutes + ':' + getSeconds,
                },
                availableGame?.token ?? null,
            );
            nextStepAction({ availableGame, endGame });
        }
    };

    const takeHint = () => {
        setCurrentStepStats({
            ...currentStepStats,
            hintsTaken: (currentStepStats.hintsTaken || 0) + 1,
        });
        saveHint(savePenalty);
    };

    const showSolution = () => {
        setCurrentStepStats({
            ...currentStepStats,
            hintsTaken: (currentStepStats.hintsTaken || 0) + 1,
        });
        saveSolutionPenalty(savePenalty);
    };

    useEffect(() => {
        if (availableGame?.snow && currentStep === 33) {
            onConfirm();
        }
    }, []);

    if (currentStep === 37 || availableGame?.finalTime !== null) {
        return (
            <GameWrapper>
                <ZdarEnding title={title} currentStep={currentStep} />
            </GameWrapper>
        );
    }

    if (isEmptyObject(stepContent) && currentStep < 37) {
        return (
            <>
                <ZdarHeader image={pageCatImage} />
                <GameWrapper>
                    <div className="card game-card">
                        <Loading color="#23ab70" />
                    </div>
                </GameWrapper>
            </>
        );
    }

    if (availableGame && currentStep < 37) {
        const lastStep = availableGame.lastStep;
        if (lastStep === null || (lastStep && currentStep < lastStep)) {
            return (
                <>
                    <ZdarHeader image={pageCatImage} />
                    <GameWrapper>
                        <div className="card game-card">
                            <Loading color="#23ab70" />
                        </div>
                    </GameWrapper>
                </>
            );
        }
    }

    if (loading && currentStep < 37) {
        return (
            <>
                <ZdarHeader image={pageCatImage} />
                <GameWrapper>
                    <div className="card game-card">
                        <Loading color="#23ab70" />
                    </div>
                </GameWrapper>
            </>
        );
    }

    return (
        <>
            <ZdarHeader image={pageCatImage} />
            <GameWrapper>
                {currentStep !== 37 && (
                    <div className="card game-card">
                        {title && (
                            <ZdarTaskTitle
                                text={title}
                                audioOpened={audioOpened}
                                setAudioOpened={onOpenAudio}
                                audio={availableGame?.snow ? snowAudio : audio}
                            />
                        )}
                        {audio && (
                            <GameAudioPlayer track={availableGame?.snow ? snowAudio : audio} opened={audioOpened} />
                        )}
                        {taskText && (
                            <ZdarTaskText
                                text={availableGame?.snow && snowTaskText ? snowTaskText + '' : taskText}
                                title={title}
                                previewText={taskPreviewText}
                                audio={availableGame?.snow ? snowAudio : audio}
                                audioOpened={audioOpened}
                                setAudioOpened={onOpenAudio}
                                snow={availableGame?.snow}
                                currentStep={currentStep}
                            />
                        )}
                        {/*// @ts-expect-error*/}
                        <ZdarCipher snow={availableGame?.snow} />
                        {currentStep !== 30 &&
                            ((cipher?.snowImages !== undefined && availableGame?.snow !== null) ||
                                cipher?.snowImages === undefined) &&
                            codeInputText && (
                                <ZdarCodeInput
                                    text={codeInputText}
                                    passCodeRegexp={codeInputPassCodeRegexp}
                                    onConfirm={onConfirm}
                                    isNumber={zdarEscapeGameNumberSteps.includes(currentStep)}
                                    wrongAnswer={onWrongAnswer}
                                />
                            )}
                        {currentStep === 30 && !availableGame?.snow && (
                            <ZdarCodeInput
                                text={codeInputText}
                                passCodeRegexp={codeInputPassCodeRegexp}
                                onConfirm={onConfirm}
                                isNumber={zdarEscapeGameNumberSteps.includes(currentStep)}
                                wrongAnswer={onWrongAnswer}
                            />
                        )}
                        {((cipher?.snowImages !== undefined && availableGame?.snow !== null) ||
                            cipher?.snowImages === undefined) && (
                            // @ts-expect-error
                            <ZdarHints takeHint={takeHint} showSolution={showSolution} snow={availableGame?.snow} />
                        )}
                        {currentStep !== 30 &&
                            ((cipher?.snowImages !== undefined && availableGame?.snow !== null) ||
                                (cipher?.snowImages === undefined && availableGame?.snow !== null)) &&
                            continueButtonText && <ContinueButton text={continueButtonText} onClick={onConfirm} />}
                        {currentStep === 30 && availableGame.snow && (
                            <ContinueButton text={'Jsme u sochy'} onClick={onConfirm} />
                        )}
                    </div>
                )}
            </GameWrapper>
        </>
    );
}

export default ZdarGame;
